import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { resizeImage } from "../../../services/screenshot.service"
import { useSol } from "../../../store/sol/hooks"
import { useModal } from "../../../store/modal/hooks"
import { renderer } from "../../viewer/Renderer"
import { useApplication } from "../../../store/application/hooks"
import { isMobile } from "../../../services/helpers.service"
import { useTabs } from "../../navigation/tabs.hook"
import {
  FEMaterial,
  SolAcousticLayerTypes,
  SolBuildingTypeTypes,
  SolConformityTypes,
  SolInsulationTypes,
  SolNoiseLevelTypes,
} from "@iko-design-center/shared"
import { updateThermalLayer } from "../../../store/sol/thermalPerformance"
import { useAccountState } from "../../providers/AccountStateProvider"
import { useQueryParams } from "../../../store/query/sol.hooks"
import { SolData } from "../../../store/sol/store"
import { Models } from "../../viewer/renderer.api"
import { ApplicationTypes } from "../../../store/application/store"

type VisibleState = {
  solScreed: boolean
  solTopLayer: boolean
  solHeating: boolean

  solInsulation: boolean

  solBottomLayer: boolean
  solBearingFloor: boolean
  solInsulationEdge: boolean
  solInnerCavityLeaf: boolean
  solType: boolean

  solNoiseLevel: boolean
  solAcousticLayer: boolean

  solConformity: boolean
  solBuildingType: boolean

  details: boolean
}

export function useOverview() {
  const account = useAccountState()
  const { t, i18n } = useTranslation() as any
  const [linkCopied, setLinkCopied] = useState<boolean>(false)
  const {
    configurationState: {
      solInsulation,
      solNoiseLevel,
      solAcousticLayer,
      solConformity,
      solBuildingType,
    },
    thermalPerformance,
    setOverviewScreenshot,
    setHumanReadableName,
    setThermalPerformance,
  } = useSol()

  const solInsulationType = solInsulation.type!
  const solNoiseLevelType = solNoiseLevel.type!
  const solAcousticLayerType = solAcousticLayer.type!
  const solConformityType = solConformity.type!
  const solBuildingTypeType = solBuildingType.type!

  const { navigateToSolNoiseLevel } = useTabs()

  const {
    displaySolDownloadModal,
    displaySolThermalCalculationModal,
    displayRequestAdviceModal,
    displaySaveConfigurationModal,
    displaySaveConfigurationSuccessModal,
  } = useModal()
  const {
    currentModel,
    applicationType,
    directLink,
    setDirectLink,
  } = useApplication()
  const { setQueryParams } = useQueryParams()

  const [thermalCalculationUpdated, setThermalCalculationUpdated] = useState(
    false
  )

  useEffect(() => {
    if (!thermalCalculationUpdated && thermalPerformance.UCValue !== null) {
      const localMaterial = { ...solInsulation.material }
      const presetThickness = localMaterial.thickness

      switch (solInsulationType) {
        case SolInsulationTypes.ENERTHERM_CHAPE_TG:
          switch (solNoiseLevelType) {
            case SolNoiseLevelTypes.DB_18:
              switch (solAcousticLayerType) {
                case SolAcousticLayerTypes.TRAMICHAPE_ECO_PRO:
                  localMaterial.thickness = 81
                  break
                case SolAcousticLayerTypes.ASSOURCHAPE_20:
                  localMaterial.thickness = 81
                  break
                default:
                  // AUCUNE
                  localMaterial.thickness = 30
                  break
              }
              break

            case SolNoiseLevelTypes.DB_19:
              switch (solAcousticLayerType) {
                case SolAcousticLayerTypes.TRAMICHAPE_ECO_PRO:
                  localMaterial.thickness = 81
                  break
                case SolAcousticLayerTypes.ASSOURCHAPE_20:
                  localMaterial.thickness = 81
                  break
                default:
                  // AUCUNE
                  localMaterial.thickness = 50
                  break
              }
              break

            case SolNoiseLevelTypes.DB_20:
              switch (solAcousticLayerType) {
                case SolAcousticLayerTypes.TRAMICHAPE_ECO_PRO:
                  localMaterial.thickness = 81
                  break
                case SolAcousticLayerTypes.ASSOURCHAPE_20:
                  localMaterial.thickness = 81
                  break
                default:
                  // AUCUNE
                  localMaterial.thickness = 54
                  break
              }
              break

            case SolNoiseLevelTypes.DB_22:
              switch (solAcousticLayerType) {
                case SolAcousticLayerTypes.TRAMICHAPE_ECO_PRO:
                  localMaterial.thickness = 81
                  break
                default:
                  // ASSOURCHAPE_20
                  localMaterial.thickness = 81
                  break
              }
              break

            case SolNoiseLevelTypes.DB_23:
              localMaterial.thickness = 81
              break

            default:
              // AUCUNE
              localMaterial.thickness = 140
              break
          }
          break
        case SolInsulationTypes.ENERTHERM_KR_ALU_SOUSDALLAGE:
          switch (solConformityType) {
            case SolConformityTypes.P1_1_1:
              switch (solBuildingTypeType) {
                case SolBuildingTypeTypes.OUI:
                  localMaterial.thickness = 120
                  break
                default:
                  // NON
                  localMaterial.thickness = 80
                  break
              }
              break
            default:
              // P1_1_2
              localMaterial.thickness = 140
              break
          }
          break
        case SolInsulationTypes.ENERTHERM_KR_ALU_DALLEPORTEE:
          localMaterial.thickness = 140
          break
      }

      if (directLink && presetThickness && presetThickness !== localMaterial.thickness) {
        localMaterial.thickness = presetThickness
      }

      setThermalCalculationUpdated(true)
      setThermalPerformance(
        updateThermalLayer(
          thermalPerformance,
          localMaterial as FEMaterial,
          "solInsulation"
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directLink, thermalPerformance])

  const initialVisibleState = useMemo(
    () => ({
      solScreed: true,
      solTopLayer: true,
      solHeating: true,

      solInsulation: true,

      solBottomLayer: true,
      solBearingFloor: true,
      solInsulationEdge: true,
      solInnerCavityLeaf: true,
      solType: true,

      solNoiseLevel: true,
      solAcousticLayer: true,

      solConformity: true,
      solBuildingType: true,

      details: true,
    }),
    []
  )
  const [visibleState, setVisibleState] = useState<VisibleState>(
    initialVisibleState
  )
  let humanReadableName = `${solInsulation.type}`

  useEffect(() => {
    setHumanReadableName(humanReadableName)
  }, [humanReadableName, setHumanReadableName])

  useEffect(() => {
    // Handle direct link
    if (renderer && currentModel === Models.DOLLHOUSE && directLink) {
      setDirectLink(false)
      setTimeout(() => {
        let model = null
        if (solInsulation.type && !solNoiseLevel.type && !solConformity.type) {
          model = Models.SOL_DALLEPORTEE
        }
        if (solInsulation.type && solNoiseLevel.type && !solConformity.type) {
          model = Models.SOL_SOUSCHAPE
        }
        if (solInsulation.type && !solNoiseLevel.type && solConformity.type) {
          model = Models.SOL_SOUSDALLAGE
        }
        if (model) {
          renderer.displayModel(model).then(() => {
            renderer.displaySolInsulationType(solInsulationType)
            renderer.toggleLayer("ISOLATIE", true)
          })
        } else {
        }
      }, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    solInsulation,
    solNoiseLevel,
    solAcousticLayer,
    solConformity,
    solBuildingType,
    setQueryParams,
    directLink,
  ])

  useEffect(() => {
    const data: SolData = {
      solInsulation,
      solNoiseLevel,
      solAcousticLayer,
      solConformity,
      solBuildingType,
      thermalPerformance,
    }
    setQueryParams(data, i18n.language)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thermalPerformance, solInsulation, solNoiseLevel, solAcousticLayer])

  useEffect(
    () => {
      if (!isMobile()) renderer.displayMarkers()
      const dataURL = renderer.createScreenshot()
      resizeImage(dataURL, 520, 420).then((resizedDataURL) => {
        setOverviewScreenshot(resizedDataURL)
      })

      return () => {
        renderer.hideMarkers()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setOverviewScreenshot]
  )

  function toggleVisibleState(key: keyof VisibleState) {
    setVisibleState({ ...visibleState, [key]: !visibleState[key] })
  }

  useEffect(() => {
    setVisibleState(initialVisibleState)
    if (!isMobile()) renderer.displayMarkers()
    renderer.toggleAllLayers(applicationType, true)
    renderer.displaySolAcousticLayerType(solAcousticLayerType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentModel, setVisibleState, initialVisibleState, applicationType])

  function handleToggleClick(toggle = "solInsulation", layer = "ISOLATIE") {
    toggleVisibleState(toggle as keyof VisibleState)
    renderer.toggleLayers(layer)
  }

  function handleDetailsClick() {
    toggleVisibleState("details")
    renderer.toggleDetails()
  }

  function handleEditNoiseLevel() {
    navigateToSolNoiseLevel()
  }

  function handleAdjustSpecificationsClick() {
    displaySolThermalCalculationModal()
  }

  function handleDownloadFilesClick() {
    displaySolDownloadModal()
  }

  function handleRequestAdviceClick() {
    displayRequestAdviceModal()
  }

  const copyShareLink = () => {
    const link = window.location.href
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(link).then(() => setLinkCopied(true))
    } else {
      window.prompt(t("flatRoof.overview.shareConfiguration"), link)
      setLinkCopied(true)
    }
  }

  function handleSaveConfigurationClick() {
    if (!account.isAuthenticated) return

    displaySaveConfigurationModal(async (name: string) => {
      const solData: SolData = {
        solInsulation: solInsulation,
        solNoiseLevel: solNoiseLevel,
        solAcousticLayer: solAcousticLayer,
        solConformity: solConformity,
        solBuildingType: solBuildingType,
        thermalPerformance: thermalPerformance,
      }
      const solConfiguration = {
        application_type: ApplicationTypes.SOL,
        application_data: solData,
        display_name: name ?? `${t("home.sol")}`,
        language: i18n.language,
      }
      account
        .saveConfiguration(solConfiguration)
        .then((response) => {
          displaySaveConfigurationSuccessModal(
            response?.saved_configuration?.record_id
          )
        })
        .catch(() => {
          console.error("Failed to save configuration")
        })
    })
  }

  function getInsulationThickness() {
    switch (solInsulationType) {
      case SolInsulationTypes.ENERTHERM_CHAPE_TG:
        switch (solNoiseLevelType) {
          case SolNoiseLevelTypes.DB_18:
            switch (solAcousticLayerType) {
              case SolAcousticLayerTypes.TRAMICHAPE_ECO_PRO:
                return "≥ 81 mm"
              case SolAcousticLayerTypes.ASSOURCHAPE_20:
                return "≥ 81 mm"
              default:
                // AUCUNE
                return "25 à 39 mm"
            }

          case SolNoiseLevelTypes.DB_19:
            switch (solAcousticLayerType) {
              case SolAcousticLayerTypes.TRAMICHAPE_ECO_PRO:
                return "≥ 81 mm"
              case SolAcousticLayerTypes.ASSOURCHAPE_20:
                return "≥ 81 mm"
              default:
                // AUCUNE
                return "40 à 53 mm"
            }

          case SolNoiseLevelTypes.DB_20:
            switch (solAcousticLayerType) {
              case SolAcousticLayerTypes.TRAMICHAPE_ECO_PRO:
                return "≥ 81 mm"
              case SolAcousticLayerTypes.ASSOURCHAPE_20:
                return "≥ 81 mm"
              default:
                // AUCUNE
                return "54 à 140 mm"
            }

          case SolNoiseLevelTypes.DB_22:
            switch (solAcousticLayerType) {
              case SolAcousticLayerTypes.TRAMICHAPE_ECO_PRO:
                return "≥ 81 mm"
              default:
                // ASSOURCHAPE_20
                return "≥ 81 mm"
            }

          case SolNoiseLevelTypes.DB_23:
            return "≥ 81 mm"
          default:
            // AUCUNE
            return "25 à 140 mm"
        }
      case SolInsulationTypes.ENERTHERM_KR_ALU_SOUSDALLAGE:
        switch (solConformityType) {
          case SolConformityTypes.P1_1_1:
            switch (solBuildingTypeType) {
              case SolBuildingTypeTypes.OUI:
                return "30 à 120 mm"
              default:
                // NON
                return "30 à 92 mm"
            }
          default:
            // P1_1_2
            return "30 à 200 mm"
        }
      case SolInsulationTypes.ENERTHERM_KR_ALU_DALLEPORTEE:
        return "30 à 200 mm"
    }
  }

  return {
    t,
    currentModel,
    thermalPerformance,
    visibleState,
    linkCopied,

    solInsulationType,
    solNoiseLevelType,
    solAcousticLayerType,
    solConformityType,
    solBuildingTypeType,

    handleToggleClick,
    handleDetailsClick,

    handleEditNoiseLevel,

    handleAdjustSpecificationsClick,
    handleDownloadFilesClick,
    handleRequestAdviceClick,

    getInsulationThickness,

    copyShareLink,
    handleSaveConfigurationClick,
  }
}
